<script setup lang="ts">
// Banner de votação no Prêmio RA1000 (Reclame Aqui) — ENG-492.
// Só renderiza a partir da abertura das votações (v-if, não display:none).
// Sem data de fim: fica ativo até nova instrução.

// `compact`: tile 48x48 só com o selo, pro rail retraído.
defineProps<{ compact?: boolean }>();

const VOTING_START_DATE = new Date('2026-09-02T00:00:00-03:00');
const VOTING_URL =
  'https://www.reclameaqui.com.br/premio/votacao/empresa/aposta-tudo/';

// Avaliado no client (app é ssr:false); rechecado a cada montagem da sidebar.
const isVotingActive = ref(false);

onMounted(() => {
  isVotingActive.value = new Date() >= VOTING_START_DATE;
});
</script>

<template>
  <a
    v-if="isVotingActive && compact"
    v-tooltip.desktopOnly="'Vote na APT no Prêmio RA1000'"
    class="ra-banner ra-banner--compact"
    :href="VOTING_URL"
    rel="noopener"
    target="_blank"
  >
    <NuxtImg
      alt="Vote na APT no Prêmio RA1000"
      class="ra-banner-seal"
      draggable="false"
      height="48"
      loading="lazy"
      :src="getAssetsUrlForCDN('/assets/images/sidebar-v2/ra1000-seal.webp')"
      width="39"
    />
    <span class="ra-banner-shine" />
  </a>

  <a
    v-else-if="isVotingActive"
    class="ra-banner"
    :href="VOTING_URL"
    rel="noopener"
    target="_blank"
  >
    <NuxtImg
      alt=""
      aria-hidden="true"
      class="ra-banner-seal"
      draggable="false"
      height="48"
      loading="lazy"
      :src="getAssetsUrlForCDN('/assets/images/sidebar-v2/ra1000-seal.webp')"
      width="39"
    />
    <span class="ra-banner-glow" />
    <span class="ra-banner-txt">
      <span class="ra-banner-title">Juntos vamos chegar longe!</span>
      <span class="ra-banner-vote">Vote na APT no Prêmio RA1000</span>
    </span>
    <NuxtIcon
      class="ra-banner-chevron"
      name="menu-lateral-v2/chevron-right"
    />
    <!-- Brilho varrendo o banner inteiro: só `transform` anima (compositor);
         o overflow-hidden do <a> recorta o feixe no raio do botão. -->
    <span class="ra-banner-shine" />
  </a>
</template>

<style scoped lang="scss">
.ra-banner {
  @apply relative flex items-center gap-[5px] w-full px-[12px] py-[6px] rounded-[14px] overflow-hidden cursor-pointer select-none;
  background:
    linear-gradient(
      90deg,
      rgba(23, 201, 100, 0.5) 13.52%,
      rgba(23, 201, 100, 0.14) 37.98%,
      rgba(23, 201, 100, 0.03) 100%
    ),
    #113320;

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.1);
    }
  }

  &--compact {
    @apply justify-center w-[48px] h-[48px] mx-auto p-0 rounded-[12px];

    .ra-banner-seal {
      @apply w-[31px] h-[38px];
    }
  }
}

.ra-banner-seal {
  @apply relative z-10 shrink-0 w-[39px] h-[48px] object-contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.45));
}

.ra-banner-glow {
  @apply absolute pointer-events-none;
  width: 120px;
  height: 56px;
  right: -48px;
  top: 1px;
  background: radial-gradient(
    ellipse at center,
    rgba(23, 201, 100, 0.38) 0%,
    rgba(23, 201, 100, 0) 70%
  );
}

.ra-banner-txt {
  @apply relative z-10 flex flex-col gap-[3px] flex-1 min-w-0;
}

.ra-banner-title {
  @apply text-white text-[11px] font-semibold leading-normal whitespace-nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
}

.ra-banner-vote {
  @apply text-[10px] font-bold leading-normal whitespace-nowrap;
  /* Glow breathing: texto fixo, halo verde acende/apaga suavemente e o verde
     clareia um pouco no pico. Anima color/text-shadow (paint), mas num texto
     de 10px o custo é desprezível. */
  color: #17c964;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.7),
    0 0 6px rgba(23, 201, 100, 0);
  animation: ra-vote-glow 3s ease-in-out infinite;
}

@keyframes ra-vote-glow {
  0%,
  100% {
    color: #17c964;
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.7),
      0 0 6px rgba(23, 201, 100, 0);
  }

  /* sobe em ~0.9s, segura no pico ~1.2s, recua em ~0.9s */
  30%,
  70% {
    color: #5ff0a0;
    text-shadow:
      0 1px 2px rgba(0, 0, 0, 0.7),
      0 0 8px rgba(23, 201, 100, 0.85),
      0 0 16px rgba(23, 201, 100, 0.45);
  }
}

.ra-banner-chevron {
  @apply relative z-10 shrink-0 text-white;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));

  :deep(svg) {
    @apply w-[13px] h-[14px];
  }
}

.ra-banner-shine {
  @apply absolute z-20 top-[-20%] bottom-[-20%] left-0 w-[35%] pointer-events-none;
  background: linear-gradient(
    105deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.18) 40%,
    rgba(255, 255, 255, 0.45) 50%,
    rgba(255, 255, 255, 0.18) 60%,
    rgba(255, 255, 255, 0) 100%
  );
  /* O main.scss zera `transition` globalmente — movimento aqui é animation. */
  animation: ra-banner-shine 3.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  will-change: transform;
}

@keyframes ra-banner-shine {
  0% {
    transform: translateX(-120%) skewX(-18deg);
  }

  40%,
  100% {
    transform: translateX(340%) skewX(-18deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ra-banner-shine {
    display: none;
  }

  .ra-banner-vote {
    animation: none;
  }
}
</style>
